/**
 * swf-size-required.js — forced size selection, enforcement layer.
 *
 * SWF 2026-08-28. Inspired by BuildMyPOD's assets/bmp-size-required.js but
 * REWRITTEN rather than ported: that module is 828 lines, imports
 * `@shopify/events` (absent from this theme's importmap) and leans on Horizon
 * 4.1.3 internals plus sticky-add-to-cart.js, which this theme does not have.
 *
 * The RENDER half already existed on this theme and was working:
 * snippets/variant-main-picker.liquid ships size unchecked, `required` on every
 * size radio, `data-is-size-option="true"` on the group and a "Please select a
 * size" placeholder on the select. It was defeated at runtime by
 * variant-picker.js#autoSelectFirstAvailableSize, now disabled. This file is
 * only the refusal + presentation.
 *
 * Two design decisions taken directly from the BMP module, both load-bearing:
 *
 * WHY `window` AND NOT `document`
 *   Horizon's declarative `on:click` dispatcher is itself a document listener
 *   registered with { capture: true } from Component.connectedCallback. Two
 *   capture listeners on the SAME node fire in REGISTRATION order, and
 *   product-form.js is imported before this file, so a document-capture
 *   listener here would run second — after the add had already been sent.
 *   `window` is the first node in the capture path, so it precedes every
 *   document listener regardless of load order.
 *
 * WHY `click` AND NOT ONLY `submit`
 *   AddToCartComponent.handleClick runs animateAddToCart() on the CLICK, before
 *   a submit event exists. Blocking only submit leaves a full success animation
 *   on a refused add. Cancelling the click suppresses the animation and the
 *   submission together. The submit listener stays as the belt for the Enter
 *   key and programmatic requestSubmit().
 *
 * Behaviour (owner spec, 2026-08-28): the button reads "Add to cart" on load.
 * A click with no size chosen is refused and the LABEL becomes "Please select
 * a size", and it stays that way — no timeout — until a size is chosen, at
 * which point the button's original label is restored from the copy stashed in
 * data-swf-size-label. Colour and the 0.4s shake are CSS keyed off
 * [data-swf-size-missing].
 */
(() => {
  const GROUP = '.variant-option[data-is-size-option="true"]';

  /** The size groups inside a given form/scope that still have no choice. */
  function unsatisfiedGroups(scope) {
    if (!scope) return [];
    return [...scope.querySelectorAll(GROUP)].filter((group) => {
      const radios = [...group.querySelectorAll('input[type="radio"]')];
      // A single-value size option offers nothing to choose. Gating it would
      // make the product permanently unbuyable.
      if (radios.length > 1) return !radios.some((r) => r.checked);
      const sel = group.querySelector('select.variant-option__select');
      if (sel && sel.options.length > 1) return sel.value === '';
      return false;
    });
  }

  /**
   * The container to judge "has a size been chosen" against.
   *
   * NOT simply `el.closest('form')`. Measured in the quick-add modal on this
   * theme: the <form> holding the add button does NOT contain the variant
   * picker — the size group is a sibling further up, inside the dialog. Scoping
   * to the form found 0 size groups, so every click looked satisfied and passed
   * straight through. Walk outward instead and take the first ancestor that
   * actually CONTAINS a size group.
   *
   * Deliberately no `body` fallback: a collection page holds many cards, and a
   * body-wide match would let one card's unchosen size block another card's
   * add. If nothing in the chain has a size group, this product is not gated.
   */
  function formScopeFor(el) {
    for (const sel of ['dialog[open]', 'quick-add-dialog', 'product-form-component', '.shopify-section']) {
      const c = el.closest(sel);
      if (c && c.querySelector(GROUP)) return c;
    }
    return null;
  }

  const PROMPT = 'Please select a size';

  /** The element holding the button's visible label. */
  function labelOf(button) {
    return button && (button.querySelector('.add-to-cart-text__content') || button);
  }

  /**
   * Swap the button's label to the prompt and remember what it said.
   *
   * The original text is stashed on the element rather than re-derived from a
   * translation key, so the button returns to whatever it actually said —
   * "Add to cart", or a merchant override — instead of a guess.
   */
  function flag(button, groups) {
    if (button) {
      const label = labelOf(button);
      if (label && !button.dataset.swfSizeLabel) {
        button.dataset.swfSizeLabel = label.textContent || '';
      }
      if (label && (label.textContent || '').trim() !== PROMPT) label.textContent = PROMPT;

      button.setAttribute('data-swf-size-missing', 'true');
      // Restart the shake if the shopper clicks again while already flagged.
      button.style.animation = 'none';
      void button.offsetWidth;
      button.style.animation = '';
    }
    groups.forEach((g) => {
      g.setAttribute('data-swf-size-missing', 'true');
      const focusable = g.querySelector('input[type="radio"], select');
      if (focusable && groups[0] === g) {
        try { focusable.focus({ preventScroll: false }); } catch (_) {}
      }
    });
  }

  /** Put every flagged button's own label back. */
  function restoreLabels(scope) {
    (scope || document).querySelectorAll('button[data-swf-size-label]').forEach((button) => {
      const label = labelOf(button);
      if (label && (label.textContent || '').trim() === PROMPT) {
        label.textContent = button.dataset.swfSizeLabel;
      }
      delete button.dataset.swfSizeLabel;
    });
  }

  function clear(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-swf-size-missing]').forEach((n) =>
      n.removeAttribute('data-swf-size-missing'));
    restoreLabels(scope);
  }

  function isAddButton(el) {
    if (!(el instanceof Element)) return false;
    return !!el.closest('add-to-cart-component button, button[name="add"], [data-swf-atc]');
  }

  // --- refusal -------------------------------------------------------------
  window.addEventListener('click', (event) => {
    const target = event.target;
    if (!isAddButton(target)) return;
    const scope = formScopeFor(target);
    const missing = unsatisfiedGroups(scope);
    if (!missing.length) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    flag(target.closest('button'), missing);
  }, true);

  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    // Only product add forms carry a size group.
    const missing = unsatisfiedGroups(formScopeFor(form));
    if (!missing.length) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    flag(form.querySelector('add-to-cart-component button, button[name="add"]'), missing);
  }, true);

  // --- the gate opens the moment a size is chosen --------------------------
  document.addEventListener('change', (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    if (!el.closest(GROUP)) return;
    const scope = formScopeFor(el);
    if (scope && !unsatisfiedGroups(scope).length) clear(scope);
  }, true);
})();
